import { useCallback, useEffect, useRef } from "react";

const LAST_EVENT_TIMESTAMP_STORAGE_KEY = "live-server-events:last-timestamp";

const normalizeTimestamp = (value) => {
  if (value === undefined || value === null) {
    return null;
  }

  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) {
    return numericValue;
  }

  const parsedDate = Date.parse(value);
  if (Number.isFinite(parsedDate)) {
    return parsedDate;
  }

  return null;
};

const getStoredLastEventTimestamp = () => {
  if (typeof localStorage === "undefined") {
    return null;
  }

  try {
    return normalizeTimestamp(localStorage.getItem(LAST_EVENT_TIMESTAMP_STORAGE_KEY));
  } catch {
    return null;
  }
};

const persistLastEventTimestamp = (timestamp) => {
  if (!timestamp || typeof localStorage === "undefined") {
    return;
  }

  try {
    localStorage.setItem(LAST_EVENT_TIMESTAMP_STORAGE_KEY, String(timestamp));
  } catch {
    // Ignore storage write errors
  }
};

const useLiveEventTimestamp = () => {
  const lastEventTimestampRef = useRef(getStoredLastEventTimestamp());

  const shouldHandleEvent = useCallback((timestamp) => {
    const normalizedTimestamp = normalizeTimestamp(timestamp);
    const lastTimestamp = lastEventTimestampRef.current;

    if (lastTimestamp === null || normalizedTimestamp === null) {
      return true;
    }

    return normalizedTimestamp >= lastTimestamp;
  }, []);

  const updateLastEventTimestamp = useCallback((timestamp) => {
    const normalizedTimestamp = normalizeTimestamp(timestamp);
    const lastTimestamp = lastEventTimestampRef.current;

    if (normalizedTimestamp === null) {
      return;
    }

    if (lastTimestamp !== null && normalizedTimestamp < lastTimestamp) {
      return;
    }

    lastEventTimestampRef.current = normalizedTimestamp;
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleBeforeUnload = () => {
      if (lastEventTimestampRef.current !== null) {
        persistLastEventTimestamp(lastEventTimestampRef.current);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  return {
    shouldHandleEvent,
    updateLastEventTimestamp,
    lastEventTimestampRef,
  };
};

export default useLiveEventTimestamp;
